import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';

import { InputMaskDirective, PredefinedInputMaskOptions } from '@blaze-shared/shared-ui';
import {
  isEmptyish,
  numericWithOptionalDecimalRegex,
  roundToPrecision,
} from '@blaze-shared/shared-util';

import { BaseCellInputComponent } from '../base-cell-input.component';

@UntilDestroy()
@Component({
  selector: 'blaze-cell-input-number',
  template: `<input
    #numberInput
    type="text"
    blazeInputMask
    predefinedMask="numberInputMask"
    [maskOptions]="blazeInputMaskOptions"
    [formControl]="displayFormCtrl"
    [class.readonly]="column.isComputed"
    [attr.readonly]="column.isComputed ? true : null"
    [readonly]="readonly"
    class="input-string"
  />`,
  styleUrls: ['../base-cell-input.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [ReactiveFormsModule, InputMaskDirective],
})
export class CellInputNumberComponent extends BaseCellInputComponent<number> implements OnInit {
  @Input() readonly = false;

  @ViewChild('numberInput') numberInputRef!: ElementRef<HTMLInputElement>;

  displayFormCtrl: FormControl = new FormControl();

  override get nativeElement(): HTMLInputElement | null {
    return this.numberInputRef?.nativeElement || null;
  }

  get blazeInputMaskOptions(): PredefinedInputMaskOptions {
    return (
      (this.column.format?.formatType === 'Percent' && {
        numberFormat: {
          format: 'percent',
          precision: this.column.precision ?? undefined,
        },
      }) ||
      (this.column.format?.formatType === 'Currency' && {
        numberFormat: {
          format: 'currency',
          precision: this.column.precision ?? undefined,
          currency: this.column.format.currencySymbol,
        },
      }) || {
        numberFormat: {
          format: 'number',
          precision: this.column.precision ?? undefined,
        },
      }
    );
  }

  get isRequired(): boolean {
    return this.column?.isRequired ?? false;
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.displayFormCtrl = this.createNumberFormControl();
  }

  private isPercentValueField(): boolean {
    return this.column?.dataType === 'Number' && this.column?.format?.formatType === 'Percent';
  }

  private getValueForDisplayedForm(value: typeof this.value): string | number | null {
    if (value === null || value === undefined) {
      return null;
    } else if (this.isPercentValueField()) {
      value = isEmptyish(value)
        ? null
        : ((value as number) * 100).toFixed(this.column.precision || 0);
    } else {
      value = roundToPrecision(Number(value), this.column.precision);
    }

    return value;
  }

  private getValueToEmit(value: string | number | null): number | null {
    let numberValue =
      typeof value === 'string' ? Number(value.match(numericWithOptionalDecimalRegex)?.[0]) : value;
    if (
      (!this.isRequired && (value === '' || value === undefined || value === null)) ||
      isEmptyish(numberValue) ||
      numberValue === null ||
      isNaN(numberValue)
    ) {
      return null;
    }

    if (this.isPercentValueField()) {
      numberValue = numberValue / 100;
    }
    return numberValue;
  }

  private createNumberFormControl(): FormControl {
    const formControl = new FormControl({
      value: this.getValueForDisplayedForm(this.value),
      disabled: this.isDisabled,
    });

    formControl.valueChanges.pipe(untilDestroyed(this)).subscribe((value) => {
      const newValue = this.getValueToEmit(value);
      this.formControl.setValue(newValue, { emitEvent: true });
    });

    return formControl;
  }
}
